import React, { useEffect, useState, useContext } from "react";
import { BiPlus } from "react-icons/bi";
import { useOutletContext } from "react-router";
import { useProject } from "../../../utils/hooks/useProject";
import API from "../../../utils/API";
import Loading from "../../../utils/components/Loading";
import { useCan } from "../../../utils/hooks/useCan";
import toastr from "toastr";
import AddNewFormDependency from "../../FormTemplates/components/FormDependency/AddNewFormDependency";
import DependencySets from "./DependencySets";
import { confirmAlert } from "react-confirm-alert";
import AlertModal from "../../../utils/components/AlertModal";
import { RequestContext } from "../../../utils/context/RequestContext";

export default function FormDependencies () {
  const [dependencies, setDependencies] = useState([]);
  const [isFetchCompleted, setIsFetchCompleted] = useState(false);
  const [modal, setModal] = useState(false);
  const { setRequestLoading, requestLoading } = useContext(RequestContext);
  
  const { project } = useProject();
  const [translate] = useOutletContext();

  const can = useCan();

  const getFormDependencies = async () => {
    setIsFetchCompleted(false);
    try {
      const res = await API.getData(`/form_dependencies?project_id=${project.id}`)

      const data = res?.data?.data || [];
      setDependencies(data);
    } catch (error) {
      console.error("error:", error);
    } finally {
      setIsFetchCompleted(true)
    }
  };

  const handleGetDependencySetDetails = async () => {
    dependencies.forEach(async (dependency) => {
      const res = await API.getData(`/form_dependency/${dependency.id}/details`)
      const data = res?.data?.data || [];
      const sets = [...dependencies];

      const index = sets.findIndex((item) => item.id === dependency.id);

      if (index > -1) {
        sets[index].details = data;
        setDependencies(sets);
      }
    });
  };

  const handleDelete = async (id, closeModal) => {
    if (requestLoading) return;
    setRequestLoading(true);

    try {
      const res = await API.deleteData(`/form_dependency/${id}`);
      toastr.success(res?.data?.message);
      getFormDependencies();
    } catch (error) {
      console.error("error:", error);
    } finally {
      closeModal();
      setRequestLoading(false);
    }
  };

  const onDelete = (id) => {
    if (!id) return false;

    confirmAlert({
      customUI: ({ onClose }) => (
        <AlertModal
          buttons={[
            {
              label: translate("cancel"),
              onClick: onClose,
              classNames: "btn btn-secondary",
            },
            {
              label: translate("delete"),
              onClick: () => {
                handleDelete(id, onClose);
              },
              classNames: "btn btn-danger",
            },
          ]}
          message={translate("delete_confirmation")}
        />
      ),
    });
  };

  const onAddButtonClick = () => {
    setModal(true);
  };

  useEffect(() => {
    if (project?.id) {
      getFormDependencies();
    }
  }, []);

  useEffect(() => {
    if (isFetchCompleted) handleGetDependencySetDetails();
  }, [isFetchCompleted]);
  
  const renderAddButton = () => (
    <div
      className="d-flex align-items-center action-button mb-4"
      role="button"
      onClick={onAddButtonClick}
    >
      <BiPlus className="fs-1 bg-primary text-white rounded-circle"/>
      <span className="ms-2 fw-medium">
        {translate("project_settings.form_dependencies.create_form_dependency_button")}
      </span>
    </div>
  );

  return (
    <div className="ProjectDependencies">
      <div className="row">
        <div className="col-12 d-flex">
          {renderAddButton()}
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <DependencySets
            sets={dependencies}
            setsLoading={false}
            handleDelete={onDelete}
            refreshFormDependencyDetails={handleGetDependencySetDetails}
          />
        </div>
      </div>

      <AddNewFormDependency
        show={modal}
        setShow={setModal}
        currentDependencySets={dependencies}
        translate={translate}
        refreshFormDependencies={getFormDependencies}
      />
    </div>
  );
}